import React, { useEffect, useState } from "react";

/**
 * ThanksBumper — a brief "★ THANK YOU ★" ident that overlays the CRT
 * when a viewer returns to the tab after clicking the pledge-drive CTA.
 * Styled like the pledge ident (SMPTE → card → fade) but recoloured to a
 * warm gold and stripped of any outbound link — this is a pure "we saw
 * you tip" moment.
 *
 * Timing (total ≈ 4.0s):
 *   -    0 - 500ms : gold-white bar sweep in
 *   -  500 - 3500ms : ★ THANK YOU ★ card holds
 *   - 3500 - 4000ms : fade back to transparent, then unmount
 *
 * Trigger logic lives in the parent (CRT.js) — see the `visibilitychange`
 * effect that watches for tab return after a pledge-CTA click.
 */
const GOLD_BARS = [
  "#fff2b0", // warm white
  "#f5c542", // gold
  "#f4a020", // amber
  "#c07a10", // deep amber
  "#f4a020",
  "#f5c542",
  "#fff2b0",
];

export function ThanksBumper({ onDismiss }) {
  const [stage, setStage] = useState("bars"); // bars | card | fade

  useEffect(() => {
    const t1 = setTimeout(() => setStage("card"), 500);
    const t2 = setTimeout(() => setStage("fade"), 3500);
    const t3 = setTimeout(() => { onDismiss && onDismiss(); }, 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDismiss]);

  const GOLD = "#f5c542";

  return (
    <div
      className="absolute inset-0 z-40 pointer-events-none"
      style={{
        opacity: stage === "fade" ? 0 : 1,
        transition: "opacity 500ms ease-out",
      }}
      data-testid="thanks-bumper"
    >
      {/* Gold-bar sweep — warm-hued cousin of the SMPTE strip. */}
      <div
        className="absolute inset-0 flex"
        style={{
          opacity: stage === "bars" ? 1 : 0,
          transition: "opacity 350ms ease-out",
        }}
        aria-hidden="true"
      >
        {GOLD_BARS.map((c, i) => (
          <div key={i} style={{ flex: 1, backgroundColor: c }} />
        ))}
      </div>

      {/* Identity card — warm-gold typography over solid black. No CTA
          button (this is a thank-you, not another pitch). */}
      <div
        className="absolute inset-0 flex items-center justify-center bg-black text-center px-8"
        style={{
          opacity: stage === "card" ? 1 : 0,
          transition: "opacity 500ms ease-in",
        }}
      >
        <div className="max-w-[92%] space-y-6">
          <div
            className="font-mono uppercase tracking-[0.35em]"
            style={{
              color: GOLD,
              fontSize: "clamp(1.5rem, 4.5vw, 2.8rem)",
              textShadow: `0 0 18px ${GOLD}, 0 0 32px ${GOLD}80`,
            }}
            data-testid="thanks-bumper-headline"
          >
            ★ THANK YOU ★
          </div>
          <div
            className="font-mono uppercase tracking-[0.2em] leading-relaxed"
            style={{
              color: GOLD,
              opacity: 0.88,
              fontSize: "clamp(0.85rem, 1.55vw, 1.15rem)",
            }}
            data-testid="thanks-bumper-subline"
          >
            YOUR TIP KEEPS THE TAPES ROLLING
          </div>
          <div
            className="font-mono uppercase tracking-[0.5em]"
            style={{
              color: GOLD,
              opacity: 0.55,
              fontSize: "clamp(0.6rem, 1vw, 0.85rem)",
            }}
          >
            SCARTLEAD.TV — VIEWER-SUPPORTED BROADCAST
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThanksBumper;
